// frontend/src/admin/v2/IntegrationsList.jsx
import { useMemo } from 'react'
import { Stack, Box, Alert, Skeleton, Button, useTheme, useMediaQuery } from '@mui/material'
import IntegrationCard from './components/IntegrationCard'
import { useIntegrations } from './hooks/useIntegrations'
import { SP } from '../../theme/tokens'
import Panel from '../../components/common/Panel'
import ListToolbar from '../../components/common/ListToolbar'
import FilterPillRow from '../../components/common/FilterPillRow'
import EmptyState from '../../components/common/EmptyState'
import RefreshButton from '../../components/common/RefreshButton'
import useListQuery from '../../components/common/listQuery/useListQuery'
import { etikett, zustandswert } from './adminStatus'

/**
 * IntegrationsList — /admin/integrations page.
 *
 * @description
 *   Card grid of the integrations declared in
 *   `backend/integrations/registry.yaml`. Quick filters in Z2 carry the same
 *   vocabulary as `/admin/jobs` and `/admin/secrets` (Healthy / Watch /
 *   Failing, `adminStatus.js`), counted client-side and disjoint over `status`.
 *
 *   Each card has a ⋮ with "Test connection", which calls the live probe and
 *   refreshes the card from the returned payload (Plan §2.3).
 *
 *   Categories aren't surfaced as section headers — kept flat for density.
 *   Will revisit when the registry grows past ~15 entries.
 */

/** Die drei Filterwerte — einer je Serverstatus, disjunkt.
 *
 * 🔴 Der WERT ist das Wort (`healthy`/`watch`/`failing`), nicht das Servertoken
 * (Plan §3.1, R16 C-1) — `zustandswert` leitet ihn aus demselben Etikett ab,
 * das die Karte rendert (W10: Pille und Karte nennen denselben Zustand gleich). */
const ZUSTAENDE = ['green', 'amber', 'red'].map(status => ({
  status,
  value: zustandswert(status),
  label: etikett(status),
}))

export default function IntegrationsList() {
  const { data, loading, error, errorTitle, probe, refetch } = useIntegrations()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const dimensionen = useMemo(() => [{
    key: 'status',
    kind: 'quick',
    label: 'State',
    values: ZUSTAENDE.map(z => ({ value: z.value, label: z.label })),
  }], [])
  const query = useListQuery({ dimensions: dimensionen })
  const { state, set, clearAll, chips } = query

  const integrationen = useMemo(() => data?.integrations || [], [data])

  const zaehler = useMemo(() => {
    const z = Object.fromEntries(ZUSTAENDE.map(zz => [zz.value, 0]))
    integrationen.forEach((it) => {
      const wert = zustandswert(it.status)
      if (z[wert] != null) z[wert] += 1
    })
    return z
  }, [integrationen])

  const gewaehlt = useMemo(
    () => (Array.isArray(state.status) ? state.status : []),
    [state.status],
  )
  const gefiltert = useMemo(
    () => (gewaehlt.length === 0
      ? integrationen
      : integrationen.filter(it => gewaehlt.includes(zustandswert(it.status)))),
    [integrationen, gewaehlt],
  )

  // 🔴 R16 W-3: hier stand `theme.breakpoints.up('md') ? 320 : 280`. `up()`
  // liefert einen Media-Query-STRING, ist also immer wahr — der 280er-Zweig
  // war unerreichbar. Die Spaltenbreite haengt allein an `isMobile`; oberhalb
  // gilt eine Zahl.
  const cols = isMobile
    ? 'repeat(1, minmax(0, 1fr))'
    : 'repeat(auto-fill, minmax(320px, 1fr))'

  const sperre = Boolean(error) && !data
  const leerGrund = sperre
    ? 'error'
    : (data && integrationen.length === 0)
      ? 'no-data'
      : (data && gefiltert.length === 0)
        ? 'no-match'
        : null

  const quickReihe = (
    <Box>
      <FilterPillRow
        alwaysShow
        multi
        label="State"
        options={ZUSTAENDE.map(z => ({ ...z, count: data ? zaehler[z.value] : undefined }))}
        value={gewaehlt}
        onChange={(v) => set('status', v)}
        disabled={!data}
      />
    </Box>
  )

  const leerzustand = leerGrund ? (
    <Box sx={{ py: SP.lg }}>
      <EmptyState
        reason={leerGrund}
        message={
          leerGrund === 'error'
            ? 'Could not load the integration list.'
            : leerGrund === 'no-data'
              // 🔴 R16 W-8: kein Repo-Pfad in der Oberflaeche — ein Satz,
              // WANN Eintraege entstehen.
              ? 'No integrations registered. Entries appear here once an integration is declared in the registry.'
              : 'No integrations match this filter.'
        }
        action={
          // 🔴 R16 W-6: nur wenn eine Pille gesetzt ist (Konzept §8.1).
          leerGrund === 'no-match' && gewaehlt.length > 0
            ? <Button variant="outlined" onClick={clearAll}>Clear all filters</Button>
            : undefined
        }
        onRetry={leerGrund === 'error' ? refetch : undefined}
        // 🔴 R16 W-2/F-3: der Env-Hinweis (403) als ZWEITE Detailzeile.
        detail={
          leerGrund === 'error'
            ? (
              <>
                {error}
                {errorTitle && <><br />{errorTitle}</>}
              </>
            )
            : undefined
        }
      />
    </Box>
  ) : null

  return (
    <Stack spacing={SP.section}>
      {error && !sperre && (
        <Alert severity="error" variant="outlined" title={errorTitle || undefined}>{error}</Alert>
      )}

      <Panel pad="card" data-testid="liste-panel" aria-busy={loading || undefined}>
        <ListToolbar
          quickFilters={quickReihe}
          // 🔴 R16 F-1/W-1: der Zaehler bleibt in `no-data`/`no-match` stehen;
          // nur der Fehler hat keinen (Hausform `ListenMuster.jsx:705`).
          count={leerGrund === 'error' ? undefined : {
            noun: 'integrations',
            n: data ? gefiltert.length : null,
            m: data?.summary?.total ?? null,
          }}
          refresh={(
            <RefreshButton
              ariaLabel="Refresh integrations"
              loading={loading}
              firstLoad={!data}
              onClick={refetch}
            />
          )}
          query={{ chips, clearAll }}
        >
          {query.ignoredValues.length > 0 && (
            <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.tight }}>
              {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
            </Alert>
          )}

          {loading && !data ? (
            <Box
              data-testid="liste-skelett"
              sx={{ display: 'grid', gridTemplateColumns: cols, gap: SP.card, mt: SP.tight }}
            >
              {[0, 1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} variant="rounded" height={220} />
              ))}
            </Box>
          ) : leerGrund ? (
            leerzustand
          ) : (
            <Box
              data-testid="liste-karte"
              sx={{ display: 'grid', gridTemplateColumns: cols, gap: SP.card, mt: SP.tight }}
            >
              {gefiltert.map((it, i) => (
                <IntegrationCard
                  key={it.key}
                  integration={it}
                  onProbe={probe}
                  delay={i * 30}
                />
              ))}
            </Box>
          )}
        </ListToolbar>
      </Panel>
    </Stack>
  )
}
